import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, Building2, CheckCircle2, RotateCcw, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("sv-SE", { dateStyle: "medium", timeStyle: "short" });
  } catch (e) {
    return "";
  }
};

const Message = ({ m, onToggle }) => {
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    setBusy(true);
    await onToggle(m);
    setBusy(false);
  };
  return (
  <article className={`p-5 sm:p-6 rounded-2xl border transition-colors ${m.handled ? "bg-[#F7F6F1] border-[#E3E2DA]" : "bg-white border-[#0B14A0]/30 shadow-[0_10px_30px_-20px_rgba(11,20,160,0.5)]"}`} data-testid={`admin-message-${m.id}`}>
    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
      <div>
        <p className="font-semibold text-lg">{m.name}</p>
        <p className="font-mono text-xs text-[#0B14A0] mt-1 uppercase tracking-[0.12em]">{m.subject}</p>
      </div>
      <div className="text-xs text-[#4A4F6E] font-mono sm:text-right">
        <p>{fmt(m.created_at)}</p>
        <p className={m.status === "sent" ? "text-emerald-700" : "text-amber-700"}>{m.status === "sent" ? "E-post skickad" : "E-post ej skickad"}</p>
      </div>
    </div>
    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#1C2150]">
      <a href={`mailto:${m.email}`} className="inline-flex items-center gap-1.5 text-[#0B14A0] hover:underline" data-testid={`admin-message-email-${m.id}`}><Mail className="w-4 h-4" />{m.email}</a>
      {m.phone && <a href={`tel:${m.phone}`} className="inline-flex items-center gap-1.5 hover:underline"><Phone className="w-4 h-4" />{m.phone}</a>}
      {m.company && <span className="inline-flex items-center gap-1.5"><Building2 className="w-4 h-4" />{m.company}</span>}
    </div>
    <p className={`mt-4 whitespace-pre-line text-[15px] leading-relaxed text-[#1C2150] rounded-xl p-4 ${m.handled ? "bg-white" : "bg-[#F7F6F1]"}`}>{m.message}</p>
    <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <p className="text-xs font-mono text-[#4A4F6E]" data-testid={`admin-message-handled-status-${m.id}`}>
        {m.handled ? `Hanterad ${fmt(m.handled_at)}` : "Att hantera"}
      </p>
      <button onClick={toggle} disabled={busy} data-testid={`admin-message-toggle-${m.id}`} className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-60 ${m.handled ? "border border-[#D8D7CE] bg-white hover:border-[#070B2E]" : "bg-[#0B14A0] text-white hover:bg-[#070B2E]"}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : m.handled ? <RotateCcw className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
        {m.handled ? "Ångra – markera som ej hanterad" : "Markera som hanterad"}
      </button>
    </div>
  </article>
  );
};

const FILTERS = [{ k: "open", t: "Att hantera" }, { k: "done", t: "Hanterade" }, { k: "all", t: "Alla" }];

export const AdminMessages = () => {
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("open");

  const onToggle = async (m) => {
    try {
      const { data } = await api.patch(`/admin/messages/${m.id}`, { handled: !m.handled });
      setItems((list) => (list || []).map((x) => (x.id === m.id ? { ...x, ...data } : x)));
      toast.success(data?.handled ? "Markerad som hanterad" : "Markerad som ej hanterad");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    api.get("/admin/messages")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch((e) => {
        toast.error(formatApiError(e));
        setItems([]);
      });
  }, []);

  if (items === null) return <p className="text-[#3A3F5C]">Laddar…</p>;
  const counts = { open: items.filter((m) => !m.handled).length, done: items.filter((m) => m.handled).length, all: items.length };
  const shown = items.filter((m) => filter === "all" || (filter === "done" ? m.handled : !m.handled));
  return (
    <div className="space-y-3" data-testid="admin-messages-list">
      <div className="flex flex-wrap gap-2 mb-5" data-testid="admin-messages-filter">
        {FILTERS.map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)} data-testid={`admin-messages-filter-${f.k}`} className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${filter === f.k ? "bg-[#0B14A0] text-white border-[#0B14A0]" : "bg-white border-[#D8D7CE] hover:border-[#070B2E]"}`}>
            {f.t}<span className={`rounded-full px-2 py-0.5 text-xs ${filter === f.k ? "bg-white/20" : "bg-[#F3F2EC]"}`} data-testid={`admin-messages-count-${f.k}`}>{counts[f.k]}</span>
          </button>
        ))}
      </div>
      {shown.length === 0 && <p className="text-[#3A3F5C]" data-testid="admin-messages-empty">{filter === "open" && items.length ? "Allt är hanterat – inga förfrågningar återstår." : "Inga meddelanden här."}</p>}
      {shown.map((m) => <Message key={m.id} m={m} onToggle={onToggle} />)}
    </div>
  );
};
